<template>
    <div>
        <div class="body">
            <l-input-text
                ref="input-name"
                v-model="element.name"
                type="text"
                :label="''"
                :placeholder="$t(`learning.admin.element.details.placeholder.name`)"
                data-test-id="element-edit-name"
            ></l-input-text>
            <l-input-text
                ref="input-description"
                v-model="element.description"
                type="textarea"
                :optional="true"
                :show-text-format-hint="true"
                :label="''"
                :placeholder="$t(`learning.admin.element.details.placeholder.description`)"
                data-test-id="element-edit-description"
            ></l-input-text>
            <l-input-typeahead
                :label="''"
                :placeholder="$t(`learning.admin.element.details.placeholder.contact`)"
                :data="dropdownItems"
                :on-select="typeaheadOnSelect"
                :on-input-change="typeaheadOnInputChange"
                data-test-id="element-edit-user-typeahead"
            >
            </l-input-typeahead>
            <span
                v-for="(user, index) in element.taggedUsers"
                v-show="element.taggedUsers.length"
                :key="user._id"
            >
                <span
                    class="label label-default clickable"
                    tabindex="0"
                    @click="removeSelected(index)"
                    @keyup.enter="removeSelected(index)"
                >
                    {{ user.name }} <i class="fa fa-remove"></i>
                </span>
            </span>
        </div>

        <br />

        <div class="footer">
            <div
                v-b-popover.hover.topleft="{
                    content: $t('learning.admin.element.details.tooltip.deleteDisabled'),
                    disabled:
                        !showDeleteBtn || !(element.usedInPaths && element.usedInPaths.length),
                }"
            >
                <button
                    v-show="showDeleteBtn"
                    class="btn btn-raised btn-default"
                    :disabled="
                        disableSubmit || Boolean(element.usedInPaths && element.usedInPaths.length)
                    "
                    data-test-id="element-delete"
                    @click="deleteElement"
                >
                    <i class="fa fa-remove"></i>
                    {{ $t("learning.admin.element.details.btn.delete") }}
                </button>
            </div>
            <div>
                <button class="btn" data-test-id="element-cancel" @click="cancel">
                    {{ $t("learning.admin.element.details.btn.back") }}
                </button>
                <button
                    class="btn btn-primary btn-raised"
                    :disabled="disableSubmit"
                    data-test-id="element-submit"
                    @click="submit"
                >
                    {{ $t("learning.admin.element.details.btn.save") }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import format from "@/utils/format-typeahead";

import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";

export default {
    components: {
        LInputText,
        LInputTypeahead,
    },
    props: {
        initialData: {
            type: Object,
            required: true,
        },
        showDeleteBtn: {
            type: Boolean,
            default: true,
        },
        disableSubmit: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            element: { ...this.initialData },
        };
    },
    computed: {
        ...mapGetters({
            usersSearchList: "users/search/list",
        }),
        dropdownItems() {
            const users = this.usersSearchList
                // Format list to fit typeahead data requirements
                .map(format.users)
                // Filter users that have already been selected
                .filter(user => !this.element.taggedUsers.find(u => u._id === user._id));

            return users;
        },
    },
    created() {
        // Properly format `this.element.taggedUsers`
        this.element.taggedUsers = (this.element.taggedUsers || []).map(format.users);
    },
    methods: {
        isValid() {
            const inputs = Object.keys(this.$refs)
                .map(key => (key.startsWith("input-") ? this.$refs[key] : null))
                .filter(Boolean);

            return inputs.reduce((a, c) => c.validate() && a, true);
        },
        typeaheadOnSelect(user) {
            if (!this.element.taggedUsers.find(u => u._id === user._id)) {
                this.element.taggedUsers.push(user);
            }

            this.$store.dispatch("users/search/reset");
        },
        typeaheadOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },
        removeSelected(index) {
            if (index < 0 || index >= this.element.taggedUsers.length) {
                return;
            }

            this.element.taggedUsers.splice(index, 1);
        },
        deleteElement() {
            this.$emit("deleteElement");
        },
        cancel() {
            this.$emit("cancel");
        },
        submit() {
            if (this.isValid()) {
                this.$emit("submit", this.element);
            }
        },
    },
};
</script>

<style scoped>
/* Label typeahead override */
.label.label-default {
    margin-right: 4px;
}

.footer {
    display: flex;
}
.footer div:first-of-type {
    flex-grow: 1;
}
</style>
